import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Avatar, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Snackbar, Stack, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ShieldIcon from "@mui/icons-material/GppGood";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import { usersApi } from "../api/resources";
import UserDialog from "../components/UserDialog";
import { useLanguage } from "../i18n/LanguageContext";
import { COLORS } from "../theme";

export default function UsuariosPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast] = useState(null);

  const load = () => usersApi.list().then(setUsers);
  // OJO: useEffect(load, []) directamente sería el bug — load() devuelve la
  // Promise de .then(), y React trataría ese valor devuelto como la función
  // de limpieza del efecto. Al desmontar (navegar a otra página), React
  // intenta *llamar* esa Promise como función → "TypeError: n is not a
  // function". Por eso el crash solo pasaba al SALIR de esta página, nunca
  // al entrar, y por eso una recarga (montaje limpio, sin desmontaje previo)
  // lo "arreglaba" sin que el bug realmente se hubiera ido.
  useEffect(() => {
    load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setDialogOpen(true);
  };

  const handleSave = async (form) => {
    if (editing) {
      const payload = { name: form.name, role: form.role };
      if (form.password) payload.password = form.password;
      await usersApi.update(editing._id, payload);
    } else {
      await usersApi.create(form);
    }
    setDialogOpen(false);
    load();
  };

  const handleDelete = async () => {
    try {
      await usersApi.remove(confirmDelete._id);
      setConfirmDelete(null);
      load();
    } catch (err) {
      setToast(err.response?.data?.error || t("userDialog.saveError"));
      setConfirmDelete(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">{t("usuarios.title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("usuarios.subtitle")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<FactCheckIcon />} onClick={() => navigate("/accesos")}>
            {t("usuarios.viewLoginHistory")}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            {t("usuarios.addUser")}
          </Button>
        </Stack>
      </Stack>

      <Alert icon={<ShieldIcon fontSize="small" />} severity="info" sx={{ mb: 2 }}>
        {t("usuarios.infoBanner")}
      </Alert>

      <Stack spacing={1}>
        {users.map((u) => (
          <Card key={u._id} sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: COLORS.brassSoft, color: "#8A5B18" }}>{u.name?.[0]?.toUpperCase()}</Avatar>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontWeight={600}>{u.name}</Typography>
                  <Chip
                    label={t(`layout.role_${u.role}`)}
                    size="small"
                    sx={{
                      bgcolor: u.role === "admin" ? COLORS.greenSoft : COLORS.brassSoft,
                      color: u.role === "admin" ? COLORS.green : "#8A5B18",
                    }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t("usuarios.username")}{u.username}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row">
              <IconButton size="small" onClick={() => openEdit(u)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => setConfirmDelete(u)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Card>
        ))}
      </Stack>

      <UserDialog open={dialogOpen} initial={editing} onClose={() => setDialogOpen(false)} onSave={handleSave} />

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("usuarios.deleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("usuarios.deleteConfirm", confirmDelete?.name, t(`layout.role_${confirmDelete?.role}`))}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>{t("common.cancel")}</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4500} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
}
